import { Badge, Button, Group, Switch, Table, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import type {
	HotelPromo,
	HotelPromoTarget,
} from "@/features/hotel-promos/types";

const TARGET_COLOR: Record<HotelPromoTarget, string> = {
	USER: "teal",
	PARTNER: "orange",
	CATEGORY: "playBlue",
};

function hotelSummary(keys: string[], titles: Record<string, string>): string {
	const names = keys.map((key) => titles[key] ?? key);
	if (names.length <= 2) {
		return names.join(", ");
	}
	return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}

export function HotelPromosTable({
	rows,
	pendingId,
	hotelTitles,
	onEdit,
	onToggle,
	onDelete,
}: {
	rows: HotelPromo[];
	pendingId?: string;
	hotelTitles: Record<string, string>;
	onEdit: (row: HotelPromo) => void;
	onToggle: (row: HotelPromo, next: boolean) => void;
	onDelete: (row: HotelPromo) => void;
}) {
	const { t } = useTranslation();
	return (
		<Table.ScrollContainer type="native" minWidth={960}>
			<Table verticalSpacing="sm" highlightOnHover>
				<Table.Thead>
					<Table.Tr>
						<Table.Th>{t("hotel.code")}</Table.Th>
						<Table.Th>{t("hotel.target")}</Table.Th>
						<Table.Th>{t("hotel.percent")}</Table.Th>
						<Table.Th>{t("hotel.hotels")}</Table.Th>
						<Table.Th>{t("hotel.active")}</Table.Th>
						<Table.Th>{t("common.created")}</Table.Th>
						<Table.Th />
					</Table.Tr>
				</Table.Thead>
				<Table.Tbody>
					{rows.map((row) => (
						<Table.Tr key={row.id}>
							<Table.Td>
								<Text fw={600}>{row.code}</Text>
							</Table.Td>
							<Table.Td>
								<Group gap="xs" wrap="nowrap">
									<Badge
										variant="light"
										size="sm"
										color={TARGET_COLOR[row.targetType]}
									>
										{t(`hotel.target_${row.targetType}`)}
									</Badge>
									<Text size="sm">{row.targetLabel}</Text>
								</Group>
							</Table.Td>
							<Table.Td>{row.percent}%</Table.Td>
							<Table.Td>
								<Text size="sm">
									{hotelSummary(row.hotelKeys, hotelTitles)}
								</Text>
							</Table.Td>
							<Table.Td>
								<Switch
									checked={row.isActive}
									disabled={pendingId === row.id}
									aria-label={`${t("hotel.active")} ${row.code}`}
									onChange={(event) =>
										onToggle(row, event.currentTarget.checked)
									}
								/>
							</Table.Td>
							<Table.Td>
								{new Date(row.createdAt).toLocaleString("uk-UA")}
							</Table.Td>
							<Table.Td>
								<Group gap="xs" justify="flex-end" wrap="nowrap">
									<Button size="xs" variant="light" onClick={() => onEdit(row)}>
										{t("common.edit")}
									</Button>
									<Button
										size="xs"
										color="red"
										variant="light"
										onClick={() => onDelete(row)}
									>
										{t("common.delete")}
									</Button>
								</Group>
							</Table.Td>
						</Table.Tr>
					))}
				</Table.Tbody>
			</Table>
		</Table.ScrollContainer>
	);
}
